declare global {
  interface Window {
    __TRANSLATIONS__?: Record<string, string>
  }
}

/**
 * Resolve a translation key against the server-injected registry.
 * Reads window.__TRANSLATIONS__ live on each call, so late-injected
 * translations still resolve. Module-level so its identity is stable across
 * renders and components — callers may safely depend on it in useCallback /
 * useEffect dependency arrays without triggering re-render loops.
 */
function translate(key: string, fallback?: string): string {
  return (window.__TRANSLATIONS__ ?? {})[key] ?? fallback ?? key
}

/**
 * Hook for accessing server-side resolved translations.
 * Translations are loaded from window.__TRANSLATIONS__ which is set by
 * react-hybrid.php layout from PHP translation registries.
 *
 * Returns a STABLE function reference (the module-level `translate`), so it is
 * safe to use as a hook/effect dependency.
 *
 * Usage:
 *   const t = useBookingT()
 *   return <h1>{t('search_cars')}</h1>
 *
 * To add a new key:
 * 1. Use t('new_key') in React component
 * 2. Add 'new_key' => Yii::t('category', 'Русский текст') to PHP registry
 * 3. Run `php yii message common/config/i18n.php`
 * 4. Translate in /vocabulary admin panel
 */
export function useBookingT() {
  return translate
}

/**
 * The same resolver for modules that are not components and so must not call a
 * `use*` function — API clients, formatters, plain helpers. Identical behaviour;
 * a separate name only so the hook rule stays readable at the call site.
 */
export { translate as translateKey }
